import { useEffect, useRef } from 'react';
import type { ValidationResponseMessage } from '../@types/common';
import type { CloneRecordsApiProps, ExportRecordFieldApiProps, RecordResult } from '../@types/records';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import type {
  AnalyzeRecordsBackgroundJobApiProps,
  BackgroundJobDetails,
  ExportFullRecordsBackgroundJobApiProps,
  ExportRecordsBackgroundJobApiProps,
  ExportSingleRecordBackgroundJobApiProps,
  ImportAttachmentsBackgroundJobProps,
  ImportFormBackgroundJobDetails,
} from '../redux/slices/background-job-slice';
import {
  apiTypeToJobType,
  JobStatus,
  JobType,
  jobTypesExcludedFromPanel,
  selectHydratedJobs,
  selectNotStartedJobs,
  setBackgroundJobDetails,
  setIsPanelOpen,
} from '../redux/slices/background-job-slice';
import { AnalyzeRecordsBackgroundJobHandler } from '../services/background-jobs/analyze-records.handler';
import { BackgroundJobHandler } from '../services/background-jobs/background-job.handler';
import { CloneRecordsBackgroundJobHandler } from '../services/background-jobs/clone-records.handler';
import { ExportFullRecordsBackgroundJobHandler } from '../services/background-jobs/export-full-records.handler';
import { ExportRecordFieldBackgroundJobHandler } from '../services/background-jobs/export-record-field.handler';
import { ExportRecordsBackgroundJobHandler } from '../services/background-jobs/export-records.handler';
import { ExportSingleRecordBackgroundJobHandler } from '../services/background-jobs/export-single-record.handler';
import { ImportFormHandler } from '../services/background-jobs/import-form.handler';
import { SaveRecordBackgroundJobHandler } from '../services/background-jobs/save-record.handler';
import { UpdateRecordBackgroundJobHandler } from '../services/background-jobs/update-record.handler';
import { forceAssert } from '../utils/type-guards';
import { ImportAttachmentsHandler } from '../services/background-jobs/import-attachments.handler';

// Only certain job types support hydration
const supportedHydrationTypes = Array.from(new Set(Object.values(apiTypeToJobType)));

// Hook to manage asynchronous background jobs
const useAsyncExportQueue = () => {
  const notStartedJobs = useAppSelector(selectNotStartedJobs); // new jobs triggered by the user
  const hydratedJobs = useAppSelector(selectHydratedJobs); // jobs that have been hydrated from persisted state
  const jobsToCheckIds = useRef<Array<number>>([]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    notStartedJobs.forEach((backgroundJobDetails) => {
      const { id, apiProps } = backgroundJobDetails;

      const backgroundJobHandler = createBackgroundJobHandler(backgroundJobDetails);

      if (!backgroundJobHandler) {
        return;
      }

      handleJobStatusUpdate(id, JobStatus.InProgress, backgroundJobHandler.details);

      backgroundJobHandler
        .createBackgroundJob(apiProps) // start the api background job
        .then((response) => {
          jobsToCheckIds.current.push(response.backgroundJobId);

          if (!jobTypesExcludedFromPanel.has(backgroundJobDetails.type)) {
            // Automatically open the background job panel
            dispatch(setIsPanelOpen(true));
          }

          const getJobDetails = () => backgroundJobHandler.getJobDetails(response.backgroundJobId);
          // the successCallback should add any data to the backgroundJobDetails if needed
          const successCallback = (response: Record<string, unknown>) => backgroundJobHandler.successCallback(response);
          const errorCallback = (responseErrors?: ValidationResponseMessage[]) =>
            backgroundJobHandler.errorCallback(responseErrors);

          checkJobStatusAndStartPolling(id, response.backgroundJobId, getJobDetails, successCallback, errorCallback);
        })
        .catch(() => {
          handleJobStatusUpdate(id, JobStatus.Failed);
          backgroundJobHandler.errorCallback();
        });
    });
  }, [notStartedJobs, dispatch]);

  useEffect(() => {
    // Handles hydrated jobs (these already have a backgroundJobId and can start polling immediately if not finished/failed)
    hydratedJobs.forEach((job) => {
      const backgroundJobId = (job.details as Record<string, unknown>)?.backgroundJobId as number;

      if (
        !backgroundJobId ||
        !supportedHydrationTypes.includes(job.type) ||
        jobsToCheckIds.current.includes(backgroundJobId)
      ) {
        return;
      }

      const backgroundJobHandler = createBackgroundJobHandler(job);

      if (!backgroundJobHandler) {
        return;
      }

      jobsToCheckIds.current.push(backgroundJobId);

      // Mark job as no longer hydrated to prevent re-processing
      dispatch(setBackgroundJobDetails({ id: job.id, details: { isHydrated: false } }));

      const getJobDetails = () => backgroundJobHandler.getJobDetails(backgroundJobId);
      // the successCallback should add any data to the backgroundJobDetails if needed
      const successCallback = (response: Record<string, unknown>) => backgroundJobHandler.successCallback(response);
      const errorCallback = (responseErrors?: ValidationResponseMessage[]) =>
        backgroundJobHandler.errorCallback(responseErrors);

      checkJobStatusAndStartPolling(job.id, backgroundJobId, getJobDetails, successCallback, errorCallback);
    });
  }, [hydratedJobs, dispatch]);

  // Function to check job status and start polling if not finished
  const checkJobStatusAndStartPolling = (
    id: string,
    backgroundJobId: number,
    getJobDetailsRequest: () => Promise<{ status: string; responseErrors?: ValidationResponseMessage[] }>,
    successCallback: (response: Record<string, unknown>) => Promise<void>,
    errorCallback: (responseErrors?: ValidationResponseMessage[]) => void
  ) => {
    getJobDetailsRequest()
      .then((jobDetails) => {
        // enrich state with jobDetails from API
        dispatch(setBackgroundJobDetails({ id, details: { ...jobDetails } }));

        const jobStatus = jobDetails?.status;

        if (jobStatus === 'FINISHED') {
          jobsToCheckIds.current = jobsToCheckIds.current.filter((id) => id !== backgroundJobId);

          successCallback(jobDetails).then(() => {
            handleJobStatusUpdate(id, JobStatus.Success);
          });
        }

        if (jobStatus === 'FAILED') {
          jobsToCheckIds.current = jobsToCheckIds.current.filter((id) => id !== backgroundJobId);
          handleJobStatusUpdate(id, JobStatus.Failed);
          errorCallback(jobDetails?.responseErrors ?? []);
        }
      })
      .catch(() => {
        jobsToCheckIds.current = jobsToCheckIds.current.filter((id) => id !== backgroundJobId);
        handleJobStatusUpdate(id, JobStatus.Failed);
        errorCallback();
      })
      .finally(() => {
        if (jobsToCheckIds.current.some((id) => id === backgroundJobId)) {
          setTimeout(
            () =>
              checkJobStatusAndStartPolling(id, backgroundJobId, getJobDetailsRequest, successCallback, errorCallback),
            6000
          );
        }
      });
  };

  const handleJobStatusUpdate = (id: string | undefined, status: JobStatus, details?: Record<string, unknown>) => {
    if (id) {
      const completedDate = status !== JobStatus.InProgress ? new Date().toISOString() : undefined;
      dispatch(setBackgroundJobDetails({ id, status, completedDate, details }));
    }
  };
};

/**
 * Function to create the appropriate BackgroundJobHandler based on job type.
 *
 * @returns An instance of BackgroundJobHandler or undefined if type is unrecognized.
 */
const createBackgroundJobHandler = (
  backgroundJobDetails: BackgroundJobDetails
): BackgroundJobHandler<unknown> | undefined => {
  const { id, type, apiProps, details } = backgroundJobDetails;

  switch (type) {
    case JobType.ExportSingleRecord:
      return new ExportSingleRecordBackgroundJobHandler(apiProps as ExportSingleRecordBackgroundJobApiProps, {
        ...details,
        jobId: id,
      });
    case JobType.ExportRecords:
      return new ExportRecordsBackgroundJobHandler(apiProps as ExportRecordsBackgroundJobApiProps, {
        ...details,
        jobId: id,
      });
    case JobType.ExportFullRecords:
      return new ExportFullRecordsBackgroundJobHandler(apiProps as ExportFullRecordsBackgroundJobApiProps, {
        ...details,
        jobId: id,
      });
    case JobType.ExportRecordField:
      return new ExportRecordFieldBackgroundJobHandler(apiProps as ExportRecordFieldApiProps, {
        ...details,
        jobId: id,
      });
    case JobType.CloneRecords:
      return new CloneRecordsBackgroundJobHandler(apiProps as CloneRecordsApiProps, {
        ...details,
        jobId: id,
      });
    case JobType.AnalyzeRecords:
      return new AnalyzeRecordsBackgroundJobHandler(apiProps as AnalyzeRecordsBackgroundJobApiProps, {
        ...details,
        jobId: id,
      });
    case JobType.ImportForm:
      return new ImportFormHandler({ ...details, id } as ImportFormBackgroundJobDetails);
    case JobType.SaveRecord:
      return new SaveRecordBackgroundJobHandler(apiProps as Omit<RecordResult, 'id'>, {
        ...details,
        jobId: id,
      });
    case JobType.UpdateRecord:
      return new UpdateRecordBackgroundJobHandler(forceAssert<RecordResult>(apiProps), {
        ...details,
        jobId: id,
      });
    case JobType.ImportAttachments:
      return new ImportAttachmentsHandler(apiProps as ImportAttachmentsBackgroundJobProps, {
        ...details,
        jobId: id,
      });
    default:
      throw new Error(`Invalid job type: ${type satisfies never}`);
  }
};

export default useAsyncExportQueue;
